import {useSidebarPageLayoutActions, useSidebarPageLayoutState} from '@github-ui/layout-primitives/SidebarPageLayout'
import {useEffect, useLayoutEffect, useRef} from 'react'

const RAILS_TRIGGER_SELECTOR = '[data-sidebar-page-layout-trigger]'

export function RailsSidebarTriggerBridge() {
  const markerRef = useRef<HTMLSpanElement>(null)
  const isDialogOpenRef = useRef(false)
  const dialogTriggerRef = useRef<HTMLButtonElement>(null)
  const {isDialogOpen} = useSidebarPageLayoutState()
  const {openDialog} = useSidebarPageLayoutActions()

  useEffect(() => {
    const container = markerRef.current?.parentElement
    if (!container) return

    const synchronizeRailsTriggers = () => {
      const triggers = Array.from(container.querySelectorAll<HTMLButtonElement>(RAILS_TRIGGER_SELECTOR))
      for (const trigger of triggers) trigger.setAttribute('aria-expanded', String(isDialogOpenRef.current))

      if (dialogTriggerRef.current && !dialogTriggerRef.current.isConnected) {
        dialogTriggerRef.current = triggers[0] ?? null
        if (dialogTriggerRef.current && isDialogOpenRef.current) openDialog(dialogTriggerRef.current)
      }
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const trigger = target.closest<HTMLButtonElement>(RAILS_TRIGGER_SELECTOR)
      if (!trigger || !container.contains(trigger)) return

      dialogTriggerRef.current = trigger
      openDialog(trigger)
    }

    synchronizeRailsTriggers()
    container.addEventListener('click', handleClick)

    const fragmentObserver = new MutationObserver(synchronizeRailsTriggers)
    fragmentObserver.observe(container, {childList: true, subtree: true})

    return () => {
      container.removeEventListener('click', handleClick)
      fragmentObserver.disconnect()
    }
  }, [openDialog])

  useLayoutEffect(() => {
    isDialogOpenRef.current = isDialogOpen
    const container = markerRef.current?.parentElement
    if (!container) return

    const triggers = container.querySelectorAll<HTMLButtonElement>(RAILS_TRIGGER_SELECTOR)
    for (const trigger of triggers) trigger.setAttribute('aria-expanded', String(isDialogOpen))
  }, [isDialogOpen])

  return <span ref={markerRef} hidden aria-hidden="true" />
}

RailsSidebarTriggerBridge.displayName = 'RailsSidebarTriggerBridge'
